import { ReactIcons, VideoControlsOverlay } from "@kalyzee/kalyzee-ui";
import { PlayerWorkBox, PlayerWorkBoxProps } from "./PlayerWorkBox";
import { MeetingSession, MeetingSessionMedia, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isSuperUser, MeetingContainerAPI, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import React, { useCallback } from "react";
import { getNormalUser } from "../../helpers/user";
import { Touchable } from "@kalyzee/kast-web-sdk/web-components";

import styles from "./MasterPlayerWorkBox.module.css";

export interface MasterPlayerWorkBoxProps {
  meetingAPI: MeetingContainerAPI;
  children: React.ReactNode;
  session: MeetingSession;
  mediaSession: MeetingSessionMedia;
  overlayMode?: PlayerWorkBoxProps["overlayMode"];
  isSmall?: boolean;
  overlayContent?: {
    type: "controls";
    onRight?: (on: boolean) => void;
    onLeft?: (on: boolean) => void;
    onUp?: (on: boolean) => void;
    onDown?: (on: boolean) => void;
    onZoomIn?: (on: boolean) => void;
    onZoomOut?: (on: boolean) => void;
  } | {
    type: "selection",
    onSelect?: () => void;
  },
  mediaControls?: boolean,
  extraHeaderContent?: React.ReactNode,  
  className?: string,
  topBarClassName?: string;
  nativeProps?: React.HTMLAttributes<HTMLDivElement>;
}

const _MasterPlayerWorkBox: React.FC<MasterPlayerWorkBoxProps> = ({
  meetingAPI,
  children,
  session,
  mediaSession,
  overlayMode,
  overlayContent,
  isSmall,
  mediaControls,
  extraHeaderContent,
  className,
  topBarClassName,
  nativeProps,
}) => {
  const [meetingOngoing] = useMeetingSelector(meetingAPI, "meeting", meeting => meeting?.state === MeetingState.RUNNING);
  const [sessionMe] = useMeetingSelector(meetingAPI, "session");
  const isMe = sessionMe?.id === session.id;

  const renderOverlayContent = () => {
    if (!overlayContent) return null;
    if (overlayContent.type === "selection") {
      return (
        <Touchable 
          onClick={overlayContent.onSelect}
          className={styles.selection}
        >
          <ReactIcons.Expand style={{ color: "white" }} />
        </Touchable>
      );
    }
    return (
      <VideoControlsOverlay
        onRight={overlayContent.onRight}
        onLeft={overlayContent.onLeft}
        onUp={overlayContent.onUp}
        onDown={overlayContent.onDown}
        onZoomIn={overlayContent.onZoomIn}
        onZoomOut={overlayContent.onZoomOut}
      >
        <></>
      </VideoControlsOverlay>
    );
  }

  const getTopBarLabel = () => {
    if (isMe) return "Vous";
    const { displayName } = session.user ? getNormalUser(session.user) : { displayName: "" };
    return displayName;
  };

  const getTobBarLabelInfo = () => {
    if (isSmall) return undefined;
    let label = mediaSession.extra?.label ?? "webcam";
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const isSuper = isSuperUser(session);

  const videoEnabled = mediaSession.video?.enabled ?? false;
  const audioEnabled = mediaSession.audio?.enabled ?? false;

  const onVideo = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      video: {
        enabled: !videoEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, videoEnabled]);

  const onAudio = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      audio: {
        enabled: !audioEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, audioEnabled]);

  return (
    <PlayerWorkBox
      label={getTopBarLabel()}
      labelInfo={getTobBarLabelInfo()}
      overlayMode={overlayMode}
      overlayContent={renderOverlayContent()}
      extraHeaderContent={extraHeaderContent}
      className={className}
      topBarClassName={topBarClassName}
      video={mediaControls ? videoEnabled : undefined}
      audio={mediaControls ? audioEnabled : undefined}
      onChangeVideo={isSuper ? onVideo : undefined}
      onChangeAudio={isSuper ? onAudio : undefined}
      isMe={isMe}
      nativeProps={nativeProps}
      idleAnimation={!meetingOngoing}
    >
      {children}
    </PlayerWorkBox>
  );
};

export const MasterPlayerWorkBox = React.memo(_MasterPlayerWorkBox);